/**
 * Harmony Icon Button Directive (hxIconButton)
 *
 * Attribute directive for icon-only buttons. Extends HxButtonBase for shared logic. Uses HxIcon component for
 * consistent icon rendering across design system.
 *
 * Supports Moon Design System specifications:
 *
 * - 4 variants: Fill, Soft, Outline, Ghost
 * - 6 contexts: Brand, Neutral, Positive, Negative, Caution, Info
 * - 5 sizes: xs (24px), sm (32px), md (40px), lg (48px), xl (56px)
 * - Optional rounded (circular) shape
 *
 * @example
 *   ```html
 *   <button hxIconButton svgIcon="close" aria-label="Close dialog"></button>
 *   <button hxIconButton svgIcon="delete" variant="outline" context="negative" aria-label="Delete"></button>
 *   <a hxIconButton href="/profile" svgIcon="user" aria-label="View profile"></a>
 *
 *   <!-- With hardcoded SVG -->
 *   <button hxIconButton variant="ghost" aria-label="Calendar">
 *     <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor">
 *       <path d="M19 3h-1V1h-2v2H8V1H6v2H5..."/>
 *     </svg>
 *   </button>
 *   ```;
 */
import {
  booleanAttribute,
  ChangeDetectionStrategy,
  Component,
  computed,
  input,
  ViewEncapsulation,
} from '@angular/core';
import { HxControlContext, HxControlSize, HxControlVariant } from '../../base/types';
import { HxButtonBase } from '../button/button-base';
import { HxIcon } from '../icon/icon';

export type ButtonVariant = Extract<HxControlVariant, 'fill' | 'soft' | 'outline' | 'ghost'>;
export type ButtonContext = HxControlContext;
export type ButtonSize = Extract<HxControlSize, 'xs' | 'sm' | 'md' | 'lg' | 'xl'>;

@Component({
  selector: 'button[hxIconButton], a[hxIconButton]',
  standalone: true,
  imports: [HxIcon],
  templateUrl: './icon-button.html',
  styleUrls: ['./icon-button-tokens.css', './icon-button.css'],
  host: {
    class: 'hx-icon-button',
    '[class]': 'buttonClasses()',
    '[attr.type]': '_getType()',
  },
  exportAs: 'hxIconButton',
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HxIconButton extends HxButtonBase {
  // Icon Button specific inputs
  readonly variant = input<ButtonVariant>('fill');
  readonly context = input<ButtonContext>('brand');
  readonly size = input<ButtonSize>('md');
  readonly rounded = input(false, { transform: booleanAttribute });
  readonly svgIcon = input<string | null>(null);
  readonly fontIcon = input<string | null>(null);
  readonly iconSize = input<string>('1em');

  // Computed signal for dynamic CSS classes
  protected readonly buttonClasses = computed(() => ({
    // Base class
    'hx-icon-button': true,

    // Variant modifier
    [`hx-icon-button--${this.variant()}`]: true,

    // Context modifier
    [`hx-icon-button--${this.context()}`]: true,

    // Size modifier
    [`hx-icon-button--${this.size()}`]: true,

    // Shape modifier
    'hx-icon-button--rounded': this.rounded(),
  }));

  // Helper to only apply type attribute to <button> elements
  protected _getType(): string | null {
    if (this._elementRef.nativeElement.tagName === 'BUTTON') {
      return 'button';
    }
    return null;
  }
}
